import { yupResolver } from "@hookform/resolvers/yup";
import { useQuery } from "@tanstack/react-query";
import { t } from "i18next";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useFieldArray, useForm, useWatch } from "react-hook-form";

import { getDataFromResponse } from "~/api/axiosUtils.ts";
import Button from "~/components/buttons/Button/Button.tsx";
import CurrencyInput from "~/components/formElements/CurrencyInput.tsx";
import FormHasErrorsHint from "~/components/formElements/FormHasErrorsHint.tsx";
import FormInputError from "~/components/formElements/FormInputError.tsx";
import FormSection, { FormSectionGroup } from "~/components/formElements/FormSection";
import SubmitButton from "~/components/formElements/SubmitButton.tsx";
import Textarea from "~/components/formElements/Textarea.tsx";
import SidebarBusyOverlay from "~/components/Sidebar/components/SidebarBusyOverlay.tsx";
import SidebarContent from "~/components/Sidebar/components/SidebarContent.tsx";
import SidebarErrorOverlay from "~/components/Sidebar/components/SidebarErrorOverlay.tsx";
import SidebarFooter from "~/components/Sidebar/components/SidebarFooter.tsx";
import SidebarHeader from "~/components/Sidebar/components/SidebarHeader.tsx";
import SidebarHeaderHeadline from "~/components/Sidebar/components/SidebarHeaderHeadline.tsx";
import { useAuth } from "~/contexts/AuthContext";
import { useFormIsSubmittable } from "~/hooks/form/useFormIsSubmittable.ts";
import { createQuota } from "~/modules/quota/api/quota/quotaApiDispatchers.ts";
import { quotaQueries } from "~/modules/quota/api/quota/quotaQueries.ts";
import type {
	CreateInvoiceBackedQuotaData,
	CreateQuotaConsumerData,
	EligibleQuotaInvoice,
	Quota,
} from "~/modules/quota/api/quota/quotaTypes.ts";
import QuotaConsumerSelector from "~/modules/quota/components/CreateQuotaSidebar/components/QuotaConsumerSelector.tsx";
import {
	QuotaInvoiceSelector,
} from "~/modules/quota/components/CreateQuotaSidebar/components/QuotaInvoiceSelector.tsx";
import QuotaConsumersFormSection from "~/modules/quota/components/formSections/QuotaConsumersFormSection.tsx";
import QuotaCoreDetailsFormSection from "~/modules/quota/components/formSections/QuotaCoreDetailsFormSection.tsx";
import { QuotaInvoicesFormSection } from "~/modules/quota/components/formSections/QuotaInvoicesFormSection.tsx";
import { useQuotaConsumerOrderInfo } from "~/modules/quota/hooks/useQuotaConsumerOrderInfo.ts";
import {
	getConsumerProjectIds,
	getCreateInvoiceBackedQuotaSchema,
	prepareCreateInvoiceBackedQuotaPayload,
} from "~/modules/quota/utils/quotaFormUtils.ts";
import { formatCentsToCurrency } from "~/utils/currencyUtils.ts";
import { preventSubmitOnEnter } from "~/utils/form/formUtils.ts";

// The invoice-backed quota form. With invoices, the amount is the sum of their excesses.
// Without invoices, the quota is a bare shell and the user types the amount.
export type CreateInvoiceBackedQuotaFormData = Omit<
	CreateInvoiceBackedQuotaData,
	"amountCents" | "invoiceIds" | "usableFrom" | "usableUntil" | "consumers"
> & {
	amountCents: number;
	invoiceIds: string[];
	usableFrom?: Date | null;
	usableUntil?: Date | null;
	consumers: CreateQuotaConsumerData[];
}

type Props = {
	companyId: string;
	onCancel: () => void;
	// The created quota and the distinct project ids of its assigned consumers.
	onSuccess: (quota: Quota, projectIds: string[]) => void;
};

type SubView = "form" | "invoiceSelect" | "orderSelect";

export const CreateInvoiceBackedQuotaForm = ({ companyId, onCancel, onSuccess }: Props) => {
	const { user } = useAuth();
	const [isBusy, setIsBusy] = useState(false);
	const [serverErrorMessage, setServerErrorMessage] = useState("");
	const [subView, setSubView] = useState<SubView>("form");

	const {
		data: eligibleInvoices,
		isLoading: eligibleInvoicesIsLoading,
	} = useQuery(quotaQueries.eligibleInvoices(companyId));

	const invoiceById = useMemo(() => {
		return new Map((eligibleInvoices ?? []).map(invoice => [invoice.id, invoice]));
	}, [eligibleInvoices]);

	const orderInfoById = useQuotaConsumerOrderInfo(companyId);

	const schema = useMemo(() => getCreateInvoiceBackedQuotaSchema(), []);

	const defaultValues = useMemo(() => {
		return {
			type: "invoice-backed" as const,
			title: "",
			managedBy: user?.id ?? "",
			clientContactPerson: "",
			comment: "",
			orderNumber: "",
			usableFrom: null,
			usableUntil: null,
			amountCents: 0,
			invoiceIds: [],
			consumers: [],
		};
	}, [user?.id]);

	const {
		handleSubmit,
		control,
		formState: { isDirty, isSubmitted, isValid, errors },
		setValue,
		trigger,
	} = useForm<CreateInvoiceBackedQuotaFormData>({
		defaultValues: defaultValues,
		resolver: yupResolver<CreateInvoiceBackedQuotaFormData>(schema),
	});

	const { fields, append, remove, replace } = useFieldArray({
		control,
		name: "consumers",
	});

	const formIsSubmittable = useFormIsSubmittable({
		isSubmitted,
		isDirty,
		isValid,
		isLoading: isBusy,
	});

	const watchedInvoiceIds = useWatch({ control, name: "invoiceIds" });
	const watchedConsumers = useWatch({ control, name: "consumers" });
	const watchedAmountCents = useWatch({ control, name: "amountCents" });
	const usableFrom = useWatch({ control, name: "usableFrom" });

	const selectedInvoices = useMemo(() => {
		return watchedInvoiceIds
			.map(invoiceId => invoiceById.get(invoiceId))
			.filter((invoice): invoice is EligibleQuotaInvoice => Boolean(invoice));
	}, [watchedInvoiceIds, invoiceById]);

	const hasInvoices = watchedInvoiceIds.length > 0;

	// An invoice-backed quota with invoices needs at least one consumer.
	const consumersMissing = hasInvoices && fields.length === 0;

	// The consumer budgets and the amount feed a cross-field rule. After the first submit,
	// re-validate the affected fields when a consumer, an invoice or the amount changes.
	// Name the nested consumer fields one by one. A no-argument trigger() clears
	// a top-level error, but it does not clear an error on a field-array path.
	useEffect(() => {
		if (!isSubmitted) {
			return;
		}
		const consumerBudgetFields = (watchedConsumers ?? []).map(
			(_, index) => `consumers.${index}.budgetCentsAssigned` as const,
		);
		void trigger(["amountCents", ...consumerBudgetFields]);
	}, [watchedConsumers, watchedAmountCents, watchedInvoiceIds, isSubmitted, trigger]);

	useEffect(() => {
		if (!isSubmitted) return;
		void trigger("usableUntil");
	}, [isSubmitted, trigger, usableFrom]);

	// Set the selected invoices. With invoices, the amount is the sum of their excesses.
	// Without invoices, reset the amount for the user to type, and drop the consumers:
	// the backend rejects consumers without invoices.
	const updateInvoiceIds = useCallback((invoiceIds: string[]) => {
		setValue("invoiceIds", invoiceIds, { shouldDirty: true });
		const excessSum = invoiceIds.reduce(
			(sum, invoiceId) => sum + (invoiceById.get(invoiceId)?.excessCents ?? 0),
			0,
		);
		setValue("amountCents", excessSum, { shouldDirty: true });
		if (invoiceIds.length === 0) {
			replace([]);
		}
	}, [invoiceById, replace, setValue]);

	const handleConfirmInvoices = useCallback((invoiceIds: string[]) => {
		updateInvoiceIds([...watchedInvoiceIds, ...invoiceIds]);
		setSubView("form");
	}, [updateInvoiceIds, watchedInvoiceIds]);

	const handleRemoveInvoice = useCallback((invoiceId: string) => {
		updateInvoiceIds(watchedInvoiceIds.filter(id => id !== invoiceId));
	}, [updateInvoiceIds, watchedInvoiceIds]);

	// The selector confirms one or more orders. Append each to the field array, then close it.
	const handleConfirmOrders = useCallback((consumers: CreateQuotaConsumerData[]) => {
		consumers.forEach(consumer => append(consumer));
		setSubView("form");
	}, [append]);

	const onSubmit = useCallback(async (data: CreateInvoiceBackedQuotaFormData) => {
		if (data.invoiceIds.length > 0 && data.consumers.length === 0) {
			return;
		}
		try {
			setIsBusy(true);

			const quotaData = prepareCreateInvoiceBackedQuotaPayload(data);
			const createdQuota = await createQuota(companyId, quotaData).then(getDataFromResponse);
			const projectIds = data.invoiceIds.length > 0
				? getConsumerProjectIds(data.consumers, orderInfoById)
				: [];
			onSuccess(createdQuota, projectIds);
		} catch (error) {
			console.log(error);
			setServerErrorMessage(t("form:unexpectedError"));
		}
	}, [companyId, onSuccess, orderInfoById]);

	return (
		<form onSubmit={handleSubmit(onSubmit)}
			  onKeyDown={preventSubmitOnEnter}
			  className="auto-grid-sidebar"
		>
			{subView === "invoiceSelect" && <QuotaInvoiceSelector invoices={eligibleInvoices}
																  isLoading={eligibleInvoicesIsLoading}
																  excludedInvoiceIds={watchedInvoiceIds}
																  onCancel={() => setSubView("form")}
																  onConfirm={handleConfirmInvoices} />}
			{subView === "orderSelect" && <QuotaConsumerSelector companyId={companyId}
																 excludedOrderIds={fields.map(field => field.orderId)}
																 onCancel={() => setSubView("form")}
																 onConfirm={handleConfirmOrders} />}
			{subView === "form" && <>
				<SidebarHeader>
					<SidebarHeaderHeadline>Neues Abrechnungskontingent</SidebarHeaderHeadline>
				</SidebarHeader>
				<SidebarContent>
					{isBusy && <SidebarBusyOverlay />}
					{!!serverErrorMessage &&
						<SidebarErrorOverlay title="Erstellung fehlgeschlagen">{serverErrorMessage}</SidebarErrorOverlay>}
					<FormSectionGroup>
						<QuotaCoreDetailsFormSection
							control={control}
							ignoreUserId={user?.id ?? ""}
						/>
						<QuotaInvoicesFormSection invoices={selectedInvoices}
												  onAddClick={() => setSubView("invoiceSelect")}
												  onRemove={handleRemoveInvoice} />
						<FormSection title="Budget">
							{hasInvoices ?
								<div>
									<div className="text-lg font-medium">{formatCentsToCurrency(watchedAmountCents)}</div>
									<div className="text-sm text-gray-500">
										Das Kontingent ergibt sich aus der Summe der Überschüsse der ausgewählten
										Rechnungen.
									</div>
									{errors.amountCents?.message &&
										<FormInputError>{errors.amountCents.message}</FormInputError>}
								</div> :
								<div className="space-y-2">
									<CurrencyInput name="amountCents"
												   useControllerError={true}
												   control={control} />
									<div className="text-sm text-gray-500">
										Ohne Rechnungen ist das Kontingent erst nutzbar, wenn ihm Rechnungen zugewiesen
										werden.
									</div>
								</div>}
						</FormSection>
						<QuotaConsumersFormSection
							control={control}
							fields={fields}
							onAddClick={() => setSubView("orderSelect")}
							onRemove={remove}
							orderInfoById={orderInfoById}
							showEmptyError={isSubmitted && consumersMissing}
							addDisabledHint={hasInvoices
								? undefined
								: "Bestellungen können erst zugewiesen werden, wenn mindestens eine Rechnung ausgewählt ist."}
						/>
						<FormSection title="Anmerkungen">
							<Textarea name="comment"
									  control={control} />
						</FormSection>
					</FormSectionGroup>
				</SidebarContent>
				<SidebarFooter>
					<FormHasErrorsHint show={isSubmitted && (!isValid || consumersMissing)}
									   className="mr-2" />
					<SubmitButton busy={isBusy}
								  disabled={!formIsSubmittable || (isSubmitted && consumersMissing)}>
						Speichern
					</SubmitButton>
					<Button theme="white"
							onClick={onCancel}>
						Abbrechen
					</Button>
				</SidebarFooter>
			</>}
		</form>);
};
